import React, { useState, useMemo } from "react";
import { Button, Stack, Typography, Link, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { useResponsive } from "../../hooks/useResponsive";
import { useThreadsAuth } from "../../hooks/useThreadsAuth";

const LoginUserSection = ({
  name,
  email,
  isSubmitting,
  handleLogin: handleLoginProp,
  handleWaitlist,
  setName,
  setEmail,
}) => {
  const { t, i18n } = useTranslation();
  const { isPC } = useResponsive();
  const { login: threadsLogin } = useThreadsAuth();
  
  // 새로운 팝업 로그인 상태 관리
  const [isThreadsLoading, setIsThreadsLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);

  // 반응형 설정
  const responsiveConfig = useMemo(() => ({
    button: {
      width: isPC ? 256 : "90%", // 모바일에서 버튼 폭 조정
      fontSize: isPC ? "1.125rem" : "1rem",
      py: isPC ? 1.5 : 1.2,
    },
    typography: {
      fontSize: isPC ? "1.125rem" : "1rem",
    },
    spacing: {
      main: isPC ? 1.5 : 1.2,
      action: isPC ? 3 : 2,
    },
  }), [isPC]);

  const handleThreadsLogin = async () => {
    setIsThreadsLoading(true);
    setLoginError(null);
    try {
      if (typeof handleLoginProp === 'function') {
        await handleLoginProp();
      } else {
        await threadsLogin();
      }
    } catch (e) {
      const msg = (e && e.message) ? e.message : (t('login.failed') || '로그인에 실패했습니다.');
      setLoginError(msg);
    } finally {
      setIsThreadsLoading(false);
    }
  };

  const termsText =
    i18n.language === "ko" ? (
      <>
        "시작하기"를 클릭하면{" "}
        <Link
          component={RouterLink}
          to="/terms"
          target="_blank"
          sx={{ fontWeight: 800, textDecoration: "underline" }}
        >
          서비스 이용약관
        </Link>
        와{" "}
        <Link
          component={RouterLink}
          to="/privacy"
          target="_blank"
          sx={{ fontWeight: 800, textDecoration: "underline" }}
        >
          개인정보 처리방침
        </Link>
        에<br />
        동의하는 것으로 간주됩니다
      </>
    ) : (
      <>
        By clicking "Get Started", you agree to our
        <br />
        <Link
          component={RouterLink}
          to="/terms"
          sx={{ fontWeight: 800, textDecoration: "underline" }}
        >
          Terms of Service
        </Link>{" "}
        and{" "}
        <Link
          component={RouterLink}
          to="/privacy"
          sx={{ fontWeight: 800, textDecoration: "underline" }}
        >
          Privacy Policy
        </Link>
      </>
    );

  return (
    <>
      {/* Beta Tester */}
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="center"
        spacing={responsiveConfig.spacing.main}
        sx={{ width: "100%" }}
      >
        {/* <Divider sx={{ width: "100%" }} /> */}
        <Stack spacing={0} alignItems="center">
          {/* <Typography variant="h2" fontWeight={900}>
            {t("landing.titleBetaTest")}
          </Typography> */}
          {/* <Typography variant="h5">
            {t("landing.descriptionBetaTest")}
          </Typography> */}
        </Stack>

        <svg
          width="24"
          height="80"
          viewBox="0 0 24 80"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.9393 79.0607C11.5251 79.6464 12.4749 79.6464 13.0607 79.0607L22.6066 69.5147C23.1924 68.9289 23.1924 67.9792 22.6066 67.3934C22.0208 66.8076 21.0711 66.8076 20.4853 67.3934L12 75.8787L3.51472 67.3934C2.92893 66.8076 1.97919 66.8076 1.3934 67.3934C0.807611 67.9792 0.807611 68.9289 1.3934 69.5147L10.9393 79.0607ZM10.5 0L10.5 78H13.5L13.5 0L10.5 0Z"
            fill="currentColor"
          />
        </svg>

        <Stack alignItems="center" spacing={responsiveConfig.spacing.action}>
          <Typography
            variant="body1"
            textAlign="center"
            sx={{
              fontSize: responsiveConfig.typography.fontSize,
              color: "text.primary",
            }}
          >
            {termsText}
          </Typography>
          {/* 에러 메시지 표시 */}
          {loginError && (
            <Typography
              variant="body2"
              textAlign="center"
              sx={{
                color: 'error.main',
                fontSize: '0.875rem',
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                padding: '8px 16px',
                borderRadius: '8px',
                maxWidth: responsiveConfig.button.width,
              }}
            >
              {loginError}
            </Typography>
          )}
          
          <Button
            variant="contained"
            size="large"
            onClick={handleThreadsLogin}
            disabled={isThreadsLoading}
            sx={{
              width: responsiveConfig.button.width,
              borderRadius: "24px",
              fontSize: responsiveConfig.button.fontSize,
              py: responsiveConfig.button.py,
              position: 'relative',
              mb: 2,
            }}
          >
            {isThreadsLoading && (
              <CircularProgress
                size={20}
                sx={{
                  color: 'inherit',
                  position: 'absolute',
                  left: '16px',
                }}
              />
            )}
            {isThreadsLoading ? t('login.loggingIn') : t("landing.getStarted")}
          </Button>

          {/* 작은 타임스탬프 표기 (캐시/빌드 갱신 확인용) */}
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', opacity: 0.7, mt: -1, mb: 1 }}
          >
            {new Date().toLocaleString()}
          </Typography>
        </Stack>
      </Stack>
    </>
  );
};

export default LoginUserSection;
